import React from 'react'
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material'

import { AppTiersAutocomplete } from '@components'
import { BACKEND_APIS } from '@constants'
import { dsColorNew } from '@palette'


// The controls of the card toolbar share one look (toolbar polish 2026-10-26): 40 px, the neutral grey border, the
// label inside; a filter in use is filled with the pill active background, never a blue border
export const TOOL_HEIGHT = 40
export const toolFieldSx = (on = false) => ({
	'& .MuiOutlinedInput-root': {
		height: TOOL_HEIGHT, borderRadius: '10px', flexWrap: 'nowrap', overflow: 'hidden',
		backgroundColor: on ? dsColorNew.pillActiveBg : dsColorNew.filterInputBg,
	},
	'& .MuiInputBase-input': { fontSize: 14 },
	'& .MuiOutlinedInput-notchedOutline': { borderColor: `${dsColorNew.grayBorder} !important`, borderWidth: '1px !important' },
	'& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: `${dsColorNew.appBlue} !important` },
})

const inlineLabel = text => <Box component="span" sx={{ color: dsColorNew.textMuted, fontSize: 13, whiteSpace: 'nowrap', pl: 0.5, pr: 0.75, flexShrink: 0 }}>{text}</Box>
const chipSx = { backgroundColor: dsColorNew.white, color: dsColorNew.appBlue, fontWeight: 500, maxWidth: 104, flexShrink: 0 }
const MAX_CHIPS = 2

/**
 * Tier and Providers of the card toolbar (CU-869fmhmx8 E2, UX D2/D3): Tiers (one) and Providers (several) choose who
 * counts as the market, on the rows already received — no server, no "Updating…". A tier fills Providers with its
 * providers that have prices here; touching Providers drops the tier. Your brand is always shown. How many are shown
 * is said once, in the card subtitle.
 *
 * `options` = `providerOptions(...)`; `shown` = the ids Providers shows as picked; `coverage` = `tierCoverage(...)`.
 */
export default function MarketBar({ tier, onTierOptions, onTier, options, shown, coverage, active, onProviders }) {
	const byId = new Map(options.map(o => [o.value, o]))
	const value = shown.map(id => byId.get(Number(id))).filter(Boolean)
	const noneHere = !!tier && !value.length

	return <>
		<Box sx={{ flex: '0 1 210px', minWidth: 170, height: TOOL_HEIGHT, ...toolFieldSx(!!tier) }}>
			<AppTiersAutocomplete
				value={tier ?? null}
				onChange={e => onTier(e.target.value ? Number(e.target.value) : null)}
				urlAjax={`${BACKEND_APIS.MASTER_DATA}/options/tiersV2`}
				onFinishAjax={onTierOptions}
				renderOption={(props, o) => <Box component="li" {...props} key={o.value} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
					<span>{o.label}</span>
					{coverage && <Typography component="small" sx={{ fontSize: 12, color: dsColorNew.textMuted, whiteSpace: 'nowrap' }}>{coverage(o).here} of {coverage(o).total} here</Typography>}
				</Box>}
				renderInput={params => <TextField {...params} placeholder="Any"
					inputProps={{ ...params.inputProps, 'aria-label': 'Tier' }}
					InputProps={{ ...params.InputProps, startAdornment: <>{inlineLabel('Tier')}{params.InputProps.startAdornment}</> }}/>}
				ListboxProps={{ 'aria-label': 'Your tiers' }}
			/>
		</Box>

		<Box sx={{ flex: '1 1 320px', minWidth: 240, maxWidth: 400, ...toolFieldSx(active) }}>
			<Autocomplete
				multiple size="small" disableCloseOnSelect
				options={options}
				value={value}
				getOptionDisabled={o => !!o.disabled}
				isOptionEqualToValue={(a, b) => a.value === b.value}
				getOptionLabel={o => o.label || ''}
				onChange={(_, picked) => onProviders(picked.filter(o => o.own !== 'primary').map(o => o.value))}
				// compact: two chips and "+N" whatever the focus, so the control keeps its height
				renderTags={(vals, getTagProps) => [
					...vals.slice(0, MAX_CHIPS).map((o, index) => <Chip size="small" label={o.label} {...getTagProps({ index })} key={o.value} sx={chipSx}/>),
					vals.length > MAX_CHIPS && <Box component="span" key="more" title={vals.slice(MAX_CHIPS).map(o => o.label).join(', ')}
						sx={{ fontSize: 13, color: dsColorNew.appBlue, fontWeight: 500, whiteSpace: 'nowrap', mx: 0.5, flexShrink: 0 }}>+{vals.length - MAX_CHIPS}</Box>,
				]}
				renderOption={(props, o, { selected }) => <Box component="li" {...props} key={o.value} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
					<span>{o.own === 'primary' ? '✓ ' : selected ? '✓ ' : ''}{o.label}</span>
					{!!o.note && <Typography component="small" sx={{ fontSize: 12, color: dsColorNew.textMuted, whiteSpace: 'nowrap' }}>{o.note}</Typography>}
				</Box>}
				renderInput={params => <TextField {...params} placeholder={value.length ? undefined : noneHere ? 'None with prices here' : 'All'}
					inputProps={{ ...params.inputProps, 'aria-label': 'Providers' }}
					InputProps={{ ...params.InputProps, startAdornment: <>{inlineLabel('Providers')}{params.InputProps.startAdornment}</> }}/>}
			/>
		</Box>
	</>
}
